import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBitcoinPrice } from '../../state/context/bitcoin.context'; // Adjust path if needed

// Import necessary MUI components
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';

function BitcoinPrice() {
    const { t } = useTranslation();
    const { price, loading, error, priceChange24h } = useBitcoinPrice();

    // --- Loading State ---
    // Show a minimal loader only if data hasn't arrived yet
    if (loading && price === null) {
        return (
            <Tooltip title={t('common.loadingBitcoinPrice')}>
                {/* Inherit color works well in AppBar */}
                <CircularProgress size={20} color="inherit" />
            </Tooltip>
        );
    }

    // --- Data Formatting and Error Handling ---
    let displayPrice = '--';
    let displayChange = '--';
    let changeColor = 'inherit'; // Default color (inherits from AppBar)
    let tooltipTitle = t('common.bitcoinPriceTooltip');

    if (price !== null) {
        // Format price compactly - no decimals necessary for typical display
        displayPrice = `$${ price.toLocaleString('en-US', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }) }`;
    }

    if (priceChange24h !== null) {
        // Format change compactly - maybe 1 decimal place
        displayChange = `${ priceChange24h >= 0 ? '+' : '' }${ priceChange24h.toFixed(1) }%`;
        // Use theme colors for light/dark mode compatibility if possible, otherwise specify colors
        // Using .light versions can sometimes work better on colored AppBars
        changeColor = priceChange24h >= 0 ? 'success.light' : 'error.light';
    }

    // Refine tooltip based on state
    if (error) {
        tooltipTitle = t('common.errorUpdatingPrice', { error });
        // If price is null due to error, ensure display reflects that
        if (price === null) displayPrice = t('common.error');
        if (priceChange24h === null) displayChange = t('common.notAvailable');
        changeColor = 'warning.light'; // Indicate issue if showing stale/error data
    } else if (loading && price !== null) {
        tooltipTitle = t('common.updatingPrice');
    } else if (price === null) {
        tooltipTitle = t('common.priceDataUnavailable');
        displayPrice = t('common.notAvailable'); // Handle unlikely case where loading finished but price is null
    }

    return (
        // Use Tooltip for details on hover
        <Tooltip title={tooltipTitle}>
            {/* Use Box for horizontal layout */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1, // Adjust spacing as needed
                    // Inherit color for base text, allows AppBar themeing to work
                    color: 'inherit',
                    // Prevent text selection if desired
                    userSelect: 'none',
                    // Indicate default cursor as it's not interactive beyond tooltip
                    cursor: 'default',
                }}
            >
                {/* Optional: Add a simple label */}
                <Typography
                    variant="body2" // Use smaller variants
                    component="span" // Render as span to stay inline
                    sx={{ fontWeight: 'medium', opacity: 0.9 }} // Slightly less prominent label
                >
                    BTC:
                </Typography>

                {/* Display Price */}
                <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 'bold' }} // Make price stand out
                >
                    {displayPrice}
                </Typography>

                {/* Display Percentage Change */}
                <Typography
                    variant="caption" // Use caption for smaller size
                    component="span"
                    sx={{
                        fontWeight: 'medium',
                        color: changeColor, // Apply conditional color
                        // Add parentheses without extra Box/span if desired
                        '&::before': { content: '"("', marginRight: '1px' },
                        '&::after': { content: '")"', marginLeft: '1px' },
                        opacity: price === null || error ? 0.7 : 1, // Dim if data is stale/missing
                    }}
                >
                    {displayChange}
                </Typography>
            </Box>
        </Tooltip>
    );
}

export default BitcoinPrice;